import React from 'react';

import { Chart, PieChartClickHandler } from './EChart';
import * as echarts from 'echarts';
import {
  commonChartConfig,
  chartGridConfig,
  groupOtherData,
  getTooltipPositionFn,
} from '../common-chart-config';
import { sumBy } from 'lodash';
import {
  DEFAULT_NUMBER_OF_SEGMENTS_FOR_BAR_CHARTS,
  OTHER_SEGMENT_NAME,
} from '../../consts';
import { widgetOperations } from '../operations';
import { HexColor, Maybe } from '@ardoq/api-types';
import { formatNumber, Locale } from '@ardoq/locale';
import { BarChartColorProps } from '../renderTypes';

type BarCharSegments = {
  segments: Array<{
    name: string;
    value: number;
    isOtherSegment: boolean;
    itemStyle?: { color: HexColor };
  }>;
  isOtherSeries: (index: number) => boolean;
  otherDataSegments: Array<{
    name: string;
    value: number;
    itemStyle?: { color: HexColor };
  }>;
};

export const getBarChartSegments = (
  data: Array<{ label: string; value: number; id?: string }>,
  numberOfBars = DEFAULT_NUMBER_OF_SEGMENTS_FOR_BAR_CHARTS
): BarCharSegments => {
  const barChartData = data
    .map(dataPoint => ({
      name: dataPoint.label,
      value: dataPoint.value,
      isOtherSegment: false,
    }))
    .filter(({ name }) => name !== '_time');

  const [visibleDataSegments, otherDataSegments, isOtherSeries] =
    groupOtherData(barChartData, numberOfBars);
  const otherDataValue = sumBy(otherDataSegments, 'value');

  const segments: Array<{
    name: string;
    value: number;
    isOtherSegment: boolean;
    itemStyle?: { color: HexColor };
  }> = [
    ...visibleDataSegments,
    ...(otherDataValue
      ? [
          {
            name: OTHER_SEGMENT_NAME,
            value: otherDataValue,
            isOtherSegment: true,
          },
        ]
      : []),
  ];
  return { segments, otherDataSegments, isOtherSeries };
};

const stringLengthLimitsBasedOnNumberOfBars: Record<number, number> = {
  2: 39,
  3: 25,
  4: 19,
  5: 14,
  6: 12,
  7: 9,
};

const MIN_LIMIT = 3;

const showAngledLabels = (
  segments: Array<{ name: string; value: number; isOtherSegment: boolean }>,
  widgetWidth: number
) => {
  const numberOfBars = segments.length;
  if (numberOfBars === 1) return false;
  if (widgetWidth === 1) return true; // These widgets are so small so it makes sense to show angled labels always
  const lengthMultiplier = widgetWidth === 3 ? 1.3 : 1;
  const limit =
    numberOfBars > 7
      ? MIN_LIMIT
      : stringLengthLimitsBasedOnNumberOfBars[numberOfBars];
  return segments.some(
    ({ name, isOtherSegment }) =>
      name.length > Math.ceil(limit * lengthMultiplier) && !isOtherSegment
  );
};

const getBarChartOptions = (
  { segments, otherDataSegments, isOtherSeries }: BarCharSegments,
  hasClickHandler: boolean,
  widgetWidth: number,
  numberFormatOptions: string,
  dashboardContainerRef: HTMLDivElement | null,
  userLocale: Locale,
  isAbbreviated: boolean
): echarts.EChartsOption => {
  const myFormatNumber = (number: number) =>
    formatNumber(number, numberFormatOptions, userLocale, isAbbreviated);
  return {
    ...commonChartConfig,
    xAxis: {
      type: 'category',
      data: segments.map(({ name }) => name),
      axisTick: {
        interval: 0,
        alignWithLabel: true,
      },
      axisLabel: {
        ...(showAngledLabels(segments, widgetWidth)
          ? {
              rotate: 30,
              width: 80,
              overflow: 'truncate',
            }
          : {
              rotate: 0,
            }),
        margin: 14,
      },
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: (value, _index) => myFormatNumber(value),
      },
    },
    tooltip: {
      extraCssText: 'white-space: normal; max-width: 80vw',
      trigger: 'item',
      confine: false,
      position: getTooltipPositionFn(dashboardContainerRef),
      formatter: ({ name, value, dataIndex }: any) =>
        `<b>${name}</b>: ${myFormatNumber(value)}${
          isOtherSeries(dataIndex)
            ? `<br /><small>${otherDataSegments
                .slice(0, 10)
                .map(
                  ({ name, value }) => `<b>${myFormatNumber(value)}</b> ${name}`
                )
                .join('<br />')}${
                otherDataSegments.length < 10
                  ? ''
                  : `<br /><i>... ${
                      otherDataSegments.length - 10
                    } more items</i>`
              }</small>`
            : ''
        }`,
    },
    series: [
      {
        type: 'bar',
        data: segments.map(({ name, value, itemStyle, isOtherSegment }) => ({
          name,
          value,
          itemStyle,
          isOtherSegment,
        })),
        ...(!hasClickHandler ? { cursor: 'default' } : {}),
      },
    ],
    grid: {
      ...chartGridConfig,
      top: '16px',
      bottom: '16px',
    },
  };
};

export type BarChartWidgetProps = {
  data: Array<{ label: string; value: number; id?: string }>;
  width: number; // in number of columns when used in a grid
  userLocale: Locale;
  numberFormatOptions?: string;
  getClickHandler?: (
    otherDataSegments: Array<{ name: string }>
  ) => PieChartClickHandler;
  numberOfBars?: number;
  isAbbreviated?: boolean;
} & BarChartColorProps;

const BarChartWidget = ({
  data,
  width,
  getClickHandler,
  dashboardContainerRef,
  numberOfBars = DEFAULT_NUMBER_OF_SEGMENTS_FOR_BAR_CHARTS,
  userLocale,
  numberFormatOptions = 'default',
  isAbbreviated = false,
  ...colorProps
}: BarChartWidgetProps & { dashboardContainerRef: Maybe<HTMLDivElement> }) => {
  const barChartSegments = getBarChartSegments(data, numberOfBars);
  const coloredSegments = barChartSegments.segments.map(segment => ({
    ...segment,
    itemStyle: {
      ...segment.itemStyle,
      color: widgetOperations.getBarChartSegmentColor(
        segment.name,
        segment.isOtherSegment,
        colorProps
      ),
    },
  }));

  const clickHandler = getClickHandler?.(barChartSegments.otherDataSegments);
  const chartOptions = getBarChartOptions(
    { ...barChartSegments, segments: coloredSegments },
    Boolean(clickHandler),
    width,
    numberFormatOptions,
    dashboardContainerRef,
    userLocale,
    isAbbreviated
  );

  return <Chart chartOptions={chartOptions} clickHandler={clickHandler} />;
};

export { BarChartWidget };
